import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchCharacterInventory,
  selectCharacter,
  selectLoading,
} from '../ViewInventory/inventorySlice'
import {
  fetchCharacterTechniques,
  learnCharacterTechnique,
} from '../../../services/characterTechniques.api'
import CharacterTechniqueCard from './components/CharacterTechniqueCard'
import './CharacterDetail.css'

const ACADEMY_TABS = [
  {
    id: 'global',
    label: 'Global',
    wipTitle: 'Técnicas globales',
    wipLead: 'El catálogo común del mundo shinobi estará disponible aquí.',
  },
  {
    id: 'academia',
    label: 'Academia',
    wipTitle: null,
    wipLead: null,
  },
  {
    id: 'clan',
    label: 'Clan',
    wipTitle: 'Técnicas de clan',
    wipLead: 'Las técnicas vinculadas a tu clan y firmas familiares llegarán con el grimorio completo.',
  },
  {
    id: 'personales',
    label: 'Personales',
    wipTitle: 'Técnicas personales',
    wipLead: 'Tus técnicas propias y estilos personalizados se gestionarán en esta sección.',
  },
]

const SHINOBI_TABS = [
  { id: 'learned', label: 'Aprendidas' },
  { id: 'learning', label: 'En entrenamiento' },
  { id: 'available', label: 'Disponibles' },
  { id: 'locked', label: 'Bloqueadas' },
  { id: 'academia', label: 'Academia' },
]

function TechniquesWipPanel({ title, lead }) {
  return (
    <section className="char-card char-card--section char-wip__panel">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">{title}</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>
      <div className="char-wip__empty">
        <div className="char-wip__seal-wrap" aria-hidden="true">
          <span className="char-wip__seal">Próximamente</span>
        </div>
        <p className="char-wip__lead">{lead}</p>
        <p className="char-wip__hint">Mientras tanto, consulta la pestaña Academia para las técnicas básicas.</p>
      </div>
    </section>
  )
}

function GrimorioSection({
  title,
  intro,
  techniques,
  emptyText,
  learningId,
  onLearn,
  canLearn = false,
  ryousBalance = 0,
}) {
  return (
    <section className="char-tech-academy">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">{title}</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>
      {intro ? <p className="char-graduation__intro">{intro}</p> : null}
      {!techniques.length ? (
        <p className="char-wip__hint">{emptyText}</p>
      ) : (
        <div className="char-tech-grid">
          {techniques.map((tech) => {
            const price = Number(tech.priceRyous) > 0 ? Number(tech.priceRyous) : 0
            const canAfford = tech.canAfford !== false && (price <= 0 || ryousBalance >= price)
            return (
              <CharacterTechniqueCard
                key={tech.techniqueId}
                technique={tech}
                canLearn={canLearn && !tech.learned && !tech.missing && canAfford}
                learning={learningId === tech.techniqueId}
                onLearn={onLearn}
                cannotAfford={canLearn && !tech.learned && !canAfford}
                defaultExpanded={false}
              />
            )
          })}
        </div>
      )}
    </section>
  )
}

export default function AcademyBasicsTechniques() {
  const { slug } = useParams()
  const dispatch = useDispatch()
  const character = useSelector(selectCharacter)
  const loadingChar = useSelector(selectLoading)
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [learningId, setLearningId] = useState(null)
  const [pendingLearn, setPendingLearn] = useState(null)

  const isAcademy = Boolean(character?.academyMode ?? payload?.academyMode)
  const [activeTab, setActiveTab] = useState(isAcademy ? 'academia' : 'learned')

  useEffect(() => {
    if (slug) dispatch(fetchCharacterInventory(slug))
  }, [slug, dispatch])

  useEffect(() => {
    setActiveTab(isAcademy ? 'academia' : 'learned')
  }, [isAcademy])

  const load = useCallback(async () => {
    if (!slug) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchCharacterTechniques(slug)
      setPayload(data)
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las técnicas')
      setPayload(null)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    if (character?.name) load()
  }, [character?.name, load])

  useEffect(() => {
    if (!pendingLearn) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape' && !learningId) setPendingLearn(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pendingLearn, learningId])

  const basics = payload?.basics || []
  const learned = payload?.learned || []
  const learningTechs = payload?.learning || []
  const available = payload?.available || []
  const locked = payload?.locked || []
  const ryousBalance = Number(payload?.ryous ?? character?.ryous ?? 0)
  const learnedCount = basics.filter((b) => b.learned).length

  const executeLearn = useCallback(
    async (techniqueId) => {
      if (!slug || learningId) return
      const pending = pendingLearn
      setPendingLearn(null)
      setLearningId(techniqueId)
      setError(null)
      try {
        const data = await learnCharacterTechnique(slug, techniqueId)
        setPayload(data)
        dispatch(fetchCharacterInventory(slug))
        if (!isAcademy) {
          const startedTraining =
            data?.learnResult?.status === 'learning' || Number(pending?.requiredDiscipline) > 0
          setActiveTab(startedTraining ? 'learning' : 'learned')
        }
      } catch (e) {
        setError(e?.message || 'No se pudo aprender la técnica')
      } finally {
        setLearningId(null)
      }
    },
    [slug, learningId, pendingLearn, dispatch, isAcademy]
  )

  const onLearn = (techniqueId) => {
    if (!slug || learningId || pendingLearn) return

    if (isAcademy) {
      void executeLearn(techniqueId)
      return
    }

    const tech =
      available.find((t) => Number(t.techniqueId) === Number(techniqueId)) ||
      basics.find((t) => Number(t.techniqueId) === Number(techniqueId))
    const price = Number(tech?.priceRyous) > 0 ? Number(tech.priceRyous) : 0
    const title = tech?.displayTitle || tech?.name || 'esta técnica'
    const requiredDiscipline = Math.max(0, Number(tech?.requiredDiscipline) || 0)
    setPendingLearn({
      techniqueId,
      title,
      price,
      remaining: Math.max(0, ryousBalance - price),
      ryousBalance,
      requiredDiscipline,
      startsTraining: requiredDiscipline > 0 || Boolean(tech?.startsTraining),
    })
  }

  const tabs = isAcademy ? ACADEMY_TABS : SHINOBI_TABS
  const tabMeta = useMemo(
    () => (isAcademy ? ACADEMY_TABS.find((t) => t.id === activeTab) : null),
    [activeTab, isAcademy]
  )

  if (loadingChar && !character?.name) {
    return (
      <div className="char-wip">
        <p className="char-wip__hint">Cargando personaje…</p>
      </div>
    )
  }

  const renderAcademyBasics = () => (
    <section className="char-tech-academy">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">Grimorio de academia</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>

      <p className="char-graduation__intro">
        {isAcademy
          ? `Las cinco técnicas básicas del estudiante. Consulta cada ficha completa; aprende las que aún no domines (${learnedCount}/${basics.length || 5}).`
          : `Las cinco técnicas básicas del camino shinobi. Fichas completas del catálogo (${learnedCount}/${basics.length || 5} dominadas).`}
      </p>

      {loading && !payload ? (
        <p className="char-wip__hint">Cargando grimorio…</p>
      ) : (
        <div className="char-tech-grid">
          {basics.map((tech) => (
            <CharacterTechniqueCard
              key={tech.techniqueId}
              technique={tech}
              canLearn={isAcademy && !tech.learned && !tech.missing}
              learning={learningId === tech.techniqueId}
              onLearn={onLearn}
              defaultExpanded
            />
          ))}
        </div>
      )}
    </section>
  )

  const renderShinobiBody = () => {
    if (loading && !payload) {
      return <p className="char-wip__hint">Cargando grimorio…</p>
    }
    if (activeTab === 'academia') return renderAcademyBasics()
    if (activeTab === 'learned') {
      return (
        <GrimorioSection
          title="Técnicas aprendidas"
          intro={`Dominas ${learned.length} técnica${learned.length === 1 ? '' : 's'} del catálogo.`}
          techniques={learned}
          emptyText="Aún no tienes técnicas aprendidas en el catálogo."
          learningId={learningId}
          onLearn={onLearn}
          ryousBalance={ryousBalance}
        />
      )
    }
    if (activeTab === 'learning') {
      return (
        <GrimorioSection
          title="En entrenamiento"
          intro="Estos jutsu aún no son tuyos del todo. Úsalos en tus aventuras o completa misiones para subir la disciplina. La barra marca el camino hasta dominarlos."
          techniques={learningTechs}
          emptyText="Ningún entrenamiento en curso. Aprende una técnica de rango superior desde Disponibles para empezar."
          learningId={learningId}
          onLearn={onLearn}
          ryousBalance={ryousBalance}
        />
      )
    }
    if (activeTab === 'available') {
      return (
        <GrimorioSection
          title="Disponibles para ti"
          intro={`Cumples los requisitos de estas técnicas. Puedes aprenderlas gastando ryous (saldo: ${ryousBalance}). Las de rango medio o alto inician un entrenamiento con disciplina.`}
          techniques={available}
          emptyText="Ninguna técnica del catálogo está disponible con tus requisitos actuales."
          learningId={learningId}
          onLearn={onLearn}
          canLearn
          ryousBalance={ryousBalance}
        />
      )
    }
    return (
      <GrimorioSection
        title="Bloqueadas"
        intro="Siguen visibles con el motivo: clan, rango, atributo o elemento. Así sabes qué aspirar."
        techniques={locked}
        emptyText="No hay técnicas bloqueadas en el catálogo."
        learningId={learningId}
        onLearn={onLearn}
        ryousBalance={ryousBalance}
      />
    )
  }

  return (
    <div className="char-wip char-tech">
      {pendingLearn ? (
        <div
          className="char-tech-confirm"
          role="presentation"
          onClick={() => {
            if (!learningId) setPendingLearn(null)
          }}
        >
          <div
            className="char-tech-confirm__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="char-tech-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="char-tech-confirm__eyebrow">
              {pendingLearn.startsTraining ? 'Comenzar entrenamiento' : 'Confirmar aprendizaje'}
            </p>
            <h3 id="char-tech-confirm-title" className="char-tech-confirm__title">
              {pendingLearn.title}
            </h3>
            {pendingLearn.startsTraining ? (
              <p className="char-tech-confirm__lead">
                Vas a comenzar el entrenamiento de este jutsu. Los ryous reservan tu plaza en el
                grimorio; para dominarlo necesitas acumular disciplina: +1 cada vez que uses la
                técnica en una aventura y +5 al completar una misión. Cuando la barra se llene, la
                técnica pasará a Aprendidas.
              </p>
            ) : pendingLearn.price > 0 ? (
              <p className="char-tech-confirm__lead">
                Vas a añadir este jutsu a tu grimorio personal. Una vez aprendido, formará parte de
                tu arsenal y el coste en ryous no se recupera. ¿Confirmas?
              </p>
            ) : (
              <p className="char-tech-confirm__lead">
                Esta técnica no tiene coste en ryous. ¿Quieres aprenderla ahora?
              </p>
            )}
            {(pendingLearn.price > 0 || pendingLearn.startsTraining) && (
              <dl className="char-tech-confirm__stats">
                {pendingLearn.price > 0 ? (
                  <>
                    <div>
                      <dt>Coste</dt>
                      <dd>{pendingLearn.price} ryous</dd>
                    </div>
                    <div>
                      <dt>Saldo actual</dt>
                      <dd>{pendingLearn.ryousBalance}</dd>
                    </div>
                    <div>
                      <dt>Tras pagar</dt>
                      <dd>{pendingLearn.remaining}</dd>
                    </div>
                  </>
                ) : null}
                {pendingLearn.startsTraining ? (
                  <div className={pendingLearn.price > 0 ? '' : undefined}>
                    <dt>Disciplina necesaria</dt>
                    <dd>{pendingLearn.requiredDiscipline}</dd>
                  </div>
                ) : null}
              </dl>
            )}
            <div className="char-tech-confirm__actions">
              <button
                type="button"
                className="char-btn char-btn--ghost"
                disabled={Boolean(learningId)}
                onClick={() => setPendingLearn(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="char-btn-accent"
                disabled={Boolean(learningId)}
                onClick={() => void executeLearn(pendingLearn.techniqueId)}
              >
                {learningId
                  ? 'Procesando…'
                  : pendingLearn.startsTraining
                    ? 'Comenzar entrenamiento'
                    : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <header className="char-wip__masthead char-tech__header">
        <div className="char-wip__masthead-accent" aria-hidden="true" />
        <div className="char-wip__masthead-body">
          <div className="char-wip__masthead-row">
            <div className="char-wip__masthead-intro">
              <span className="char-wip__masthead-mark" aria-hidden="true">
                術
              </span>
              <div className="char-wip__masthead-text">
                <p className="char-wip__masthead-eyebrow">Arte ninja</p>
                <h2 className="char-wip__title">Técnicas</h2>
              </div>
            </div>
            {!isAcademy && payload ? (
              <p className="char-tech-ryous" title="Ryous disponibles">
                <span className="char-tech-ryous__label">Ryous</span>
                <span className="char-tech-ryous__value">{ryousBalance}</span>
              </p>
            ) : null}
          </div>

          <nav className="char-tech-subnav" aria-label="Categorías de técnicas">
            <div className="char-tech-subnav__list" role="tablist">
              {tabs.map((tab) => {
                const selected = activeTab === tab.id
                const soon = isAcademy && tab.id !== 'academia'
                const count =
                  !isAcademy && tab.id === 'learned'
                    ? learned.length
                    : !isAcademy && tab.id === 'learning'
                      ? learningTechs.length
                      : !isAcademy && tab.id === 'available'
                        ? available.length
                        : !isAcademy && tab.id === 'locked'
                          ? locked.length
                          : null
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={`char-tech-subnav__tab${selected ? ' is-active' : ''}${soon ? ' is-soon' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span className="char-tech-subnav__label">{tab.label}</span>
                    {count != null ? (
                      <span className="char-tech-subnav__count">{count}</span>
                    ) : null}
                    {soon ? <span className="char-tech-subnav__badge">próx.</span> : null}
                  </button>
                )
              })}
            </div>
          </nav>
        </div>
      </header>

      <div className="char-wip__body char-tech__scroll">
        {error ? (
          <p className="char-graduation__hint" role="alert">
            {error}
          </p>
        ) : null}

        {isAcademy ? (
          activeTab !== 'academia' ? (
            <TechniquesWipPanel title={tabMeta?.wipTitle} lead={tabMeta?.wipLead} />
          ) : (
            renderAcademyBasics()
          )
        ) : (
          renderShinobiBody()
        )}

        <div className="char-graduation__footer">
          <Link to={`/personajes/${slug}/ficha`} className="char-btn char-btn--ghost">
            Volver a la ficha
          </Link>
        </div>
      </div>
    </div>
  )
}
